import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

const useElementOperations = ({
  screens,
  setScreens,
  currentScreenIndex,
  selectedElement,
  setSelectedElement,
  snapPosition,
  setConfirmDialog,
  setSaveStatus,
}) => {
  const [lockedElements, setLockedElements] = useState(new Set());
  const [elementGroups, setElementGroups] = useState([]);
  const [showOutOfBoundsWarning, setShowOutOfBoundsWarning] = useState(false);

  const updateElementPosition = useCallback((elementId, newPosition) => {
    setScreens(prevScreens => {
      const currentScreen = prevScreens[currentScreenIndex];
      const updatedElements = currentScreen.elements.map(el =>
        el.id === elementId ? { ...el, position: newPosition } : el
      );

      const updatedScreen = { ...currentScreen, elements: updatedElements };
      const updatedScreens = [...prevScreens];
      updatedScreens[currentScreenIndex] = updatedScreen;
      return updatedScreens;
    });
  }, [currentScreenIndex, setScreens]);

  // Batch-update several elements' positions in a single setScreens write so a
  // group move / nudge / paste is one history entry, not N element-by-element
  // steps that undo would replay one at a time (FECOA-1523).
  const updateElementPositions = useCallback((updates) => {
    if (!updates?.length) return;
    const byId = new Map(updates.map((u) => [u.id, u.position]));
    setScreens(prevScreens => {
      const currentScreen = prevScreens[currentScreenIndex];
      const updatedElements = currentScreen.elements.map(el =>
        byId.has(el.id) ? { ...el, position: byId.get(el.id) } : el
      );
      const updatedScreen = { ...currentScreen, elements: updatedElements };
      const updatedScreens = [...prevScreens];
      updatedScreens[currentScreenIndex] = updatedScreen;
      return updatedScreens;
    });
  }, [currentScreenIndex, setScreens]);

  const updateElementSize = (elementId, newSize) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { ...el, size: newSize } : el
    );

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    setScreens(updatedScreens);
  };

  const updateElementContent = (elementId, newContent) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el => {
      if (el.id === elementId) {
        // For question elements, update both content and data.text for consistency
        if (el.type === 'question') {
          return { 
            ...el, 
            content: newContent,
            data: { ...el.data, text: newContent }
          };
        }
        // For other elements, just update content
        return { ...el, content: newContent };
      }
      return el;
    });

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    setScreens(updatedScreens);
  };

  const updateElementStyle = (elementId, styleUpdates) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { 
        ...el, 
        style: { ...el.style, ...styleUpdates } 
      } : el
    );

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    setScreens(updatedScreens);
  };

  const updateResponseConfig = (elementId, configUpdates) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { 
        ...el, 
        responseConfig: { ...el.responseConfig, ...configUpdates } 
      } : el
    );

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    setScreens(updatedScreens);
    setSaveStatus('unsaved');
  };

  const alignElement = (elementId, alignment) => {
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element) {
      console.error('Element not found:', elementId);
      return;
    }

    const canvasWidth = currentScreen.layout.width || 1200;
    const canvasHeight = currentScreen.layout.height || 800;
    const elementWidth = element.size.width;
    const elementHeight = element.size.height;

    console.log('=== ALIGNMENT DEBUG ===');
    console.log('Alignment type:', alignment);
    console.log('Canvas dimensions:', canvasWidth, 'x', canvasHeight);
    console.log('Element dimensions:', elementWidth, 'x', elementHeight);
    console.log('Current position:', element.position);
    console.log('Screen layout:', currentScreen.layout);

    let newPosition = { ...element.position };

    switch (alignment) {
      // Horizontal alignments
      case 'left':
        newPosition.x = snapPosition(20);
        break;
      case 'center-h':
        newPosition.x = snapPosition((canvasWidth - elementWidth) / 2);
        break;
      case 'right':
        newPosition.x = snapPosition(canvasWidth - elementWidth - 20);
        break;
      case 'third-left':
        newPosition.x = snapPosition(canvasWidth / 3 - elementWidth / 2);
        break;
      case 'third-right':
        newPosition.x = snapPosition((canvasWidth * 2 / 3) - elementWidth / 2);
        break;
      case 'quarter-1':
        newPosition.x = snapPosition(canvasWidth / 4 - elementWidth / 2);
        break;
      case 'quarter-3':
        newPosition.x = snapPosition((canvasWidth * 3 / 4) - elementWidth / 2);
        break;
      
      // Vertical alignments
      case 'top':
        newPosition.y = snapPosition(20);
        break;
      case 'center-v':
        newPosition.y = snapPosition((canvasHeight - elementHeight) / 2);
        break;
      case 'bottom':
        newPosition.y = snapPosition(canvasHeight - elementHeight - 20);
        break;
      case 'third-top':
        newPosition.y = snapPosition(canvasHeight / 3 - elementHeight / 2);
        break;
      case 'third-bottom':
        newPosition.y = snapPosition((canvasHeight * 2 / 3) - elementHeight / 2);
        break;
      
      // Corner positions
      case 'top-left':
        newPosition.x = snapPosition(20);
        newPosition.y = snapPosition(20);
        break;
      case 'top-center':
        newPosition.x = snapPosition((canvasWidth - elementWidth) / 2);
        newPosition.y = snapPosition(20);
        break;
      case 'top-right':
        newPosition.x = snapPosition(canvasWidth - elementWidth - 20);
        newPosition.y = snapPosition(20);
        break;
      case 'middle-left':
        newPosition.x = snapPosition(20);
        newPosition.y = snapPosition((canvasHeight - elementHeight) / 2);
        break;
      case 'middle-right':
        newPosition.x = snapPosition(canvasWidth - elementWidth - 20);
        newPosition.y = snapPosition((canvasHeight - elementHeight) / 2);
        break;
      case 'bottom-left':
        newPosition.x = snapPosition(20);
        newPosition.y = snapPosition(canvasHeight - elementHeight - 20);
        break;
      case 'bottom-center':
        newPosition.x = snapPosition((canvasWidth - elementWidth) / 2);
        newPosition.y = snapPosition(canvasHeight - elementHeight - 20);
        break;
      case 'bottom-right':
        newPosition.x = snapPosition(canvasWidth - elementWidth - 20);
        newPosition.y = snapPosition(canvasHeight - elementHeight - 20);
        break;
      
      // Split screen positions
      case 'left-half':
        newPosition.x = snapPosition(canvasWidth / 4 - elementWidth / 2);
        newPosition.y = snapPosition((canvasHeight - elementHeight) / 2);
        break;
      case 'right-half':
        newPosition.x = snapPosition((canvasWidth * 3 / 4) - elementWidth / 2);
        newPosition.y = snapPosition((canvasHeight - elementHeight) / 2);
          break;
      
      // Center both
      case 'center':
        const centerX = (canvasWidth - elementWidth) / 2;
        const centerY = (canvasHeight - elementHeight) / 2;
        console.log('Center calculation:');
        console.log('  X: (', canvasWidth, '-', elementWidth, ') / 2 =', centerX);
        console.log('  Y: (', canvasHeight, '-', elementHeight, ') / 2 =', centerY);
        newPosition.x = snapPosition(centerX);
        newPosition.y = snapPosition(centerY);
        console.log('  After snap:', newPosition);
        break;
      
      default:
        break;
    }

    console.log('Final new position:', newPosition);
    console.log('======================');
    updateElementPosition(elementId, newPosition);
  };

  // Check if any elements are outside canvas bounds
  const getOutOfBoundsElements = useCallback(() => {
    const currentScreen = screens[currentScreenIndex];
    if (!currentScreen?.elements?.length) return [];
    
    const canvasWidth = currentScreen.layout?.width || 800;
    const canvasHeight = currentScreen.layout?.height || 600;
    
    return currentScreen.elements.filter(element => {
      const x = element.position?.x || 0;
      const y = element.position?.y || 0;
      const width = element.size?.width || 100;
      const height = element.size?.height || 50;
      
      // Element is out of bounds if any part extends beyond canvas
      return x < 0 || y < 0 || 
             x + width > canvasWidth + 50 || // Allow 50px overflow for partial visibility
             y + height > canvasHeight + 50;
    });
  }, [screens, currentScreenIndex]);

  // Fit elements to canvas. Locked elements are never moved, resized, or even
  // counted in the fit math. In-bounds elements are left exactly where they are
  // unless the content is too large to fit (proportional mode) — then the whole
  // movable group is scaled down. Otherwise only stray (off-canvas) elements are
  // pulled back into view.
  // Pure per-screen fit: returns { elements, changed, lockedStray } and never
  // touches state or toasts, so single-screen and all-screens callers share it.
  const fitScreenElementsOnce = useCallback((targetScreen, scalingMode) => {
    const canvasWidth = targetScreen.layout?.width || 800;
    const canvasHeight = targetScreen.layout?.height || 600;
    const padding = 20;

    const getRect = (el) => ({
      x: el.position?.x || 0,
      y: el.position?.y || 0,
      width: el.size?.width || 100,
      height: el.size?.height || 50,
    });
    const isOffCanvas = (el) => {
      const { x, y, width, height } = getRect(el);
      return x < -1 || y < -1 || x + width > canvasWidth + 1 || y + height > canvasHeight + 1;
    };

    // Locked elements are immune — exclude them from the fit entirely.
    const movable = targetScreen.elements.filter((el) => !el.isLocked);
    if (movable.length === 0) {
      return { elements: targetScreen.elements, changed: 0, lockedStray: targetScreen.elements.some(isOffCanvas) };
    }

    // Bounding box of the movable elements only.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    movable.forEach((el) => {
      const { x, y, width, height } = getRect(el);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const availableWidth = canvasWidth - padding * 2;
    const availableHeight = canvasHeight - padding * 2;
    const scaleX = contentWidth > availableWidth ? availableWidth / contentWidth : 1;
    const scaleY = contentHeight > availableHeight ? availableHeight / contentHeight : 1;
    const scale = Math.min(scaleX, scaleY, 1);

    // Only scale/reflow the whole group when content genuinely can't fit.
    const mustScale = scalingMode === 'proportional' && scale < 1;

    const clampInside = (val, maxPos) => {
      const snapped = snapPosition(Math.min(Math.max(val, 0), maxPos));
      return Math.min(Math.max(snapped, 0), maxPos);
    };

    let changed = 0;
    const elements = targetScreen.elements.map((element) => {
      if (element.isLocked) return element;
      const { x, y, width, height } = getRect(element);
      let newX, newY, newWidth, newHeight;

      if (mustScale) {
        newX = snapPosition((x - minX) * scale + padding);
        newY = snapPosition((y - minY) * scale + padding);
        newWidth = Math.max(50, Math.round(width * scale));
        newHeight = Math.max(30, Math.round(height * scale));
      } else {
        // Leave in-bounds elements untouched; pull only stray ones back in.
        newWidth = width;
        newHeight = height;
        if (isOffCanvas(element) && element.type === 'button') {
          // Rescued navigation buttons take the standard spots (Back
          // bottom-left, Next bottom-right, mirroring auto-build defaults)
          // instead of wherever a stale device-sized template left them.
          const isBack = element.buttonType === 'back';
          newX = isBack ? 50 : Math.max(0, canvasWidth - width - 50);
          newY = Math.max(0, canvasHeight - height - 40);
        } else {
          const maxXPos = Math.max(0, canvasWidth - width);
          const maxYPos = Math.max(0, canvasHeight - height);
          newX = (x < 0 || x + width > canvasWidth) ? clampInside(x, maxXPos) : x;
          newY = (y < 0 || y + height > canvasHeight) ? clampInside(y, maxYPos) : y;
        }
      }

      if (newX === x && newY === y && newWidth === width && newHeight === height) {
        return element;
      }
      changed += 1;
      return { ...element, position: { x: newX, y: newY }, size: { width: newWidth, height: newHeight } };
    });

    const lockedStray = changed === 0 && targetScreen.elements.some((el) => el.isLocked && isOffCanvas(el));
    return { elements, changed, lockedStray };
  }, [snapPosition]);

  const fitElementsToCanvas = useCallback((scalingMode = 'reposition', screenIndex = currentScreenIndex) => {
    const targetScreen = screens[screenIndex];
    if (!targetScreen?.elements?.length) {
      toast.info('No elements to adjust');
      return;
    }

    const { elements, changed, lockedStray } = fitScreenElementsOnce(targetScreen, scalingMode);

    if (changed === 0) {
      toast.info(lockedStray
        ? 'Off-canvas elements are locked — unlock them to auto-fit'
        : 'Everything already fits the canvas');
      return;
    }

    const updatedScreens = [...screens];
    updatedScreens[screenIndex] = { ...targetScreen, elements };
    setScreens(updatedScreens);

    toast.success(`Adjusted ${changed} element${changed !== 1 ? 's' : ''} to fit canvas`);
  }, [screens, currentScreenIndex, fitScreenElementsOnce, setScreens]);

  const fitAllScreensToCanvas = useCallback((scalingMode = 'reposition') => {
    let totalChanged = 0;
    let screensChanged = 0;
    const updatedScreens = screens.map((screen) => {
      if (!screen?.elements?.length) return screen;
      const { elements, changed } = fitScreenElementsOnce(screen, scalingMode);
      if (changed === 0) return screen;
      totalChanged += changed;
      screensChanged += 1;
      return { ...screen, elements };
    });

    if (screensChanged === 0) {
      toast.info('Everything already fits the canvas');
      return;
    }

    setScreens(updatedScreens);
    toast.success(
      `Adjusted ${totalChanged} element${totalChanged !== 1 ? 's' : ''} across ${screensChanged} screen${screensChanged !== 1 ? 's' : ''}`
    );
  }, [screens, fitScreenElementsOnce, setScreens]);

  const deleteElement = (elementId) => {
    // Check if element is locked
    if (lockedElements.has(elementId)) {
      toast.warning('Cannot delete a locked element. Unlock it first.');
      return;
    }
    
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    const elementType = element?.type || 'element';
    // Get descriptive text based on element type
    const elementDescription = element?.data?.text || element?.label || element?.content || element?.code || '';
    const screenLabel = currentScreen?.name || currentScreen?.id || `Screen ${currentScreenIndex + 1}`;
    
    // Only show the quoted description if there's actual content
    const descriptionText = elementDescription ? `\n\n"${elementDescription}"` : '';
    
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${elementType}?`,
      message: `Are you sure you want to delete this ${elementType} from screen "${screenLabel}"?${descriptionText}`,
      type: 'danger',
      onConfirm: () => {
        const updatedElements = currentScreen.elements.filter(el => el.id !== elementId);
    
        const updatedScreen = { ...currentScreen, elements: updatedElements };
        const updatedScreens = [...screens];
        updatedScreens[currentScreenIndex] = updatedScreen;
        setScreens(updatedScreens);
        
        // Clear selection
        if (selectedElement === elementId) {
          setSelectedElement(null);
        }
        
        // Remove from any groups
        setElementGroups(prev => prev.filter(group => !group.includes(elementId)));
      }
    });
  };

  // Lock/Unlock functions
  const toggleLockElement = (elementId) => {
    // Update element's isLocked property in screens state
    setScreens(prevScreens => {
      return prevScreens.map((screen, index) => {
        if (index === currentScreenIndex) {
          return {
            ...screen,
            elements: screen.elements.map(el => {
              if (el.id === elementId) {
                return { ...el, isLocked: !el.isLocked };
              }
              return el;
            })
          };
        }
        return screen;
      });
    });
    
    // Also update the Set for immediate reactivity
    const newLockedElements = new Set(lockedElements);
    if (newLockedElements.has(elementId)) {
      newLockedElements.delete(elementId);
    } else {
      newLockedElements.add(elementId);
    }
    setLockedElements(newLockedElements);
  };

  const isElementLocked = (elementId) => {
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen?.elements?.find(el => el.id === elementId);
    return element?.isLocked || false;
  };

  // Ungroup question into separately movable sub-elements.
  // Measures the currently-rendered grouped DOM so sub-elements land at the
  // exact pixel positions the browser already laid out — ungrouping is meant
  // to be a visual no-op (clinical assessments: every pixel is deliberate).
  const ungroupQuestion = (elementId) => {
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element || element.type !== 'question') return;

    // The grouped DOM is still mounted at this point (this handler runs before
    // the re-render). Measure it now so both the fresh-measurement path and
    // the re-ungroup rebase below can read current rects.
    //
    // getBoundingClientRect returns post-transform (zoomed) pixels, but the
    // CSS width/left/top we write back are logical pixels. Divide by the
    // canvas zoom so measurements round-trip correctly at any zoom level.
    const rootEl = typeof document !== 'undefined'
      ? document.querySelector(`[data-element="${CSS.escape(elementId)}"]`)
      : null;
    const rootRect = rootEl?.getBoundingClientRect();
    const zoom = rootRect && element.size.width
      ? rootRect.width / element.size.width
      : 1;
    const innerEl = rootEl?.querySelector('[data-question-inner]');
    const innerRect = innerEl?.getBoundingClientRect();
    const measure = (el) => {
      if (!el || !innerRect || !zoom) return null;
      const r = el.getBoundingClientRect();
      // Preserve fractional pixels. CSS accepts sub-pixel values (e.g.
      // "left: 135.72px") and the browser renders them precisely. Rounding
      // here introduces up to 0.5px drift per axis per element — visible on
      // clinical canvases. snapPosition during drag still snaps to whole
      // pixels, so user-edited positions stay clean.
      return {
        x: (r.left - innerRect.left) / zoom,
        y: (r.top - innerRect.top) / zoom,
        width: r.width / zoom,
        height: r.height / zoom,
      };
    };

    // Re-ungroup rebase: existing subs and the option count still matches
    // grouped. Re-measure each sub against the *current* grouped DOM and shift
    // by the cached drag/resize delta. This keeps Custom Layout pixel-aligned
    // to the grouped render after responseConfig edits (buttonStyle, padding,
    // fontSize, borderWidth, ...) while preserving manual drags the author
    // applied in the ungrouped state. Without this, the cache restores stale
    // positions sized for the prior config and drifts against the inner button
    // which paints with current config.
    const existingSubs = element.subElements || [];
    if (existingSubs.length > 0) {
      const groupedOptionCount = (element.responseOptions || element.data?.options || []).length;
      const existingOptionSubs = existingSubs.filter(s => s.type === 'question-option');
      const hasScaleSub = existingSubs.some(s => s.type !== 'question-text' && s.type !== 'question-option');
      if (hasScaleSub || existingOptionSubs.length === groupedOptionCount) {
        const rebased = existingSubs.map(sub => {
          let newRect = null;
          if (sub.type === 'question-text') {
            newRect = measure(innerEl?.querySelector('[data-question-text]'));
          } else if (sub.type === 'question-option') {
            if (typeof sub.optionIndex === 'number') {
              newRect = measure(innerEl?.querySelector(`[data-option-index="${sub.optionIndex}"]`));
            }
          } else {
            newRect = measure(innerEl?.querySelector('[data-scale-content]'));
          }

          if (!newRect) return sub;

          const oldRect = sub.originalMeasured;
          if (!oldRect) {
            // Sub predates this baseline (cached before the rebase shipped).
            // Adopt the fresh rect as the baseline without disturbing the
            // author's position — drag deltas take effect on next re-ungroup.
            return { ...sub, originalMeasured: newRect };
          }

          const dx = sub.position.x - oldRect.x;
          const dy = sub.position.y - oldRect.y;
          const dw = sub.size.width - oldRect.width;
          const dh = sub.size.height - oldRect.height;

          return {
            ...sub,
            position: { x: newRect.x + dx, y: newRect.y + dy },
            size: { width: newRect.width + dw, height: newRect.height + dh },
            originalMeasured: newRect,
            measured: true,
          };
        });

        const restored = currentScreen.elements.map(el =>
          el.id === elementId ? { ...el, isUngrouped: true, subElements: rebased } : el
        );
        const updatedScreens = [...screens];
        updatedScreens[currentScreenIndex] = { ...currentScreen, elements: restored };
        setScreens(updatedScreens);
        return;
      }
    }

    const fallbackTextHeight = 60;
    const fallbackOptionHeight = 32;
    const spacing = element.responseConfig?.spacing || 8;
    const layout = element.responseConfig?.layout;
    const orientation = element.responseConfig?.orientation;
    const isNumeric = element.data?.type === 'numeric';

    // Layouts that render as a single scale/widget block rather than per-option
    // sub-elements. If the author picked a scale layout (vas, thermometer,
    // etc.) we respect it. Otherwise, numeric-type questions default to NRS
    // because the grouped render paints NRS for `data.type === 'numeric'`
    // regardless of layout — ungroup must match or the scale disappears.
    const scaleLayoutMap = {
      vas: { subType: 'question-vas', height: 80 },
      nrs: { subType: 'question-nrs', height: orientation === 'vertical' ? 320 : 80 },
      'vrs-unipolar': { subType: 'question-vrs', height: orientation === 'horizontal' ? 80 : 240 },
      'vrs-bipolar': { subType: 'question-vrs', height: orientation === 'horizontal' ? 80 : 240 },
      segmented: { subType: 'question-segmented', height: 60 },
      emoji: { subType: 'question-emoji', height: 100 },
      thermometer: { subType: 'question-thermometer', height: 320 },
      'stepped-slider': { subType: 'question-stepped-slider', height: 80 },
      text: { subType: 'question-text-input', height: 100 },
    };
    const effectiveLayout = scaleLayoutMap[layout]
      ? layout
      : (isNumeric ? 'nrs' : null);
    const scaleDef = effectiveLayout ? scaleLayoutMap[effectiveLayout] : null;

    const textMeas = measure(innerEl?.querySelector('[data-question-text]'));
    const textY = textMeas?.y ?? 0;
    const textHeight = textMeas?.height ?? fallbackTextHeight;

    const scaleMeas = scaleDef ? measure(innerEl?.querySelector('[data-scale-content]')) : null;
    const scaleSubElement = scaleDef ? [{
      id: `${elementId}_${scaleDef.subType.replace('question-', '')}`,
      type: scaleDef.subType,
      parentId: elementId,
      position: {
        x: scaleMeas?.x ?? 0,
        y: scaleMeas?.y ?? textY + textHeight + 20,
      },
      size: {
        width: scaleMeas?.width ?? element.size.width,
        height: scaleMeas?.height ?? scaleDef.height,
      },
      measured: Boolean(scaleMeas),
      ...(scaleMeas ? { originalMeasured: scaleMeas } : {}),
    }] : [];

    const optionSubs = scaleDef ? [] : (element.responseOptions || element.data.options || []).map((option, idx) => {
      const optMeas = measure(innerEl?.querySelector(`[data-option-index="${idx}"]`));
      const fallbackY = textY + textHeight + 20 + (idx * (fallbackOptionHeight + spacing));
      return {
        id: `${elementId}_option_${idx}`,
        type: 'question-option',
        parentId: elementId,
        optionIndex: idx,
        content: typeof option === 'string' ? option : (option.label || option),
        value: typeof option === 'object' ? option.value : idx,
        position: {
          x: optMeas?.x ?? 0,
          y: optMeas?.y ?? fallbackY,
        },
        size: {
          width: optMeas?.width ?? element.size.width,
          height: optMeas?.height ?? fallbackOptionHeight,
        },
        style: { fontSize: element.responseConfig?.optionFontSize || 14 },
        measured: Boolean(optMeas),
        ...(optMeas ? { originalMeasured: optMeas } : {}),
      };
    });

    const subElements = [
      {
        // Question text fills its parent block. Use the measured <p> width
        // (which equals the inner container's content width) so centered
        // text lines up with grouped mode — element.size.width would be
        // wider by the element's border (2px each side) and shift the
        // centered title a pixel or two to the right.
        id: `${elementId}_text`,
        type: 'question-text',
        parentId: elementId,
        content: element.data.text,
        position: { x: textMeas?.x ?? 0, y: textY },
        size: {
          width: textMeas?.width ?? element.size.width,
          height: textHeight,
        },
        style: { ...element.style },
        measured: Boolean(textMeas),
        ...(textMeas ? { originalMeasured: textMeas } : {}),
      },
      ...scaleSubElement,
      ...optionSubs,
    ];

    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId
        ? { ...el, isUngrouped: true, subElements }
        : el
    );

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  // Regroup question back into single element. Keep subElements so the author's
  // Custom Layout edits survive the toggle — ungroupQuestion will restore them
  // on re-entry instead of re-measuring from grouped DOM.
  const regroupQuestion = (elementId) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId
        ? { ...el, isUngrouped: false }
        : el
    );

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  // Update sub-element position (relative to parent)
  const updateSubElementPosition = (parentId, subElementId, newPosition) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el => {
      if (el.id === parentId && el.subElements) {
        return {
          ...el,
          subElements: el.subElements.map(sub =>
            sub.id === subElementId
              ? { ...sub, position: newPosition }
              : sub
          )
        };
      }
      return el;
    });

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  // Update sub-element size
  const updateSubElementSize = (parentId, subElementId, newSize) => {
    const currentScreen = screens[currentScreenIndex];
    const updatedElements = currentScreen.elements.map(el => {
      if (el.id === parentId && el.subElements) {
        return {
          ...el,
          subElements: el.subElements.map(sub =>
            sub.id === subElementId
              ? { ...sub, size: newSize }
              : sub
          )
        };
      }
      return el;
    });

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  // Ungrouped Layout Control Functions
  const autoArrangeOptions = (elementId, layout = 'vertical', spacing = 40) => {
    console.log('🎯 autoArrangeOptions called:', { elementId, layout, spacing });
    
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    
    if (!element || !element.isUngrouped || !element.subElements) return;

    const questionText = element.subElements.find(sub => sub.type === 'question-text');
    const options = element.subElements.filter(sub => sub.type === 'question-option');
    
    if (!questionText || options.length === 0) return;

    // Calculate starting position (below question text)
    const startX = questionText.position.x;
    const startY = questionText.position.y + questionText.size.height + spacing;
    
    let updatedSubElements = [...element.subElements];
    
    if (layout === 'vertical') {
      options.forEach((option, idx) => {
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = {
            x: startX,
            y: startY + (idx * (option.size.height + spacing))
          };
        }
      });
    } else if (layout === 'horizontal') {
      options.forEach((option, idx) => {
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = {
            x: startX + (idx * (option.size.width + spacing)),
            y: startY
          };
        }
      });
    } else if (layout === 'grid') {
      const columns = 2;
      options.forEach((option, idx) => {
        const row = Math.floor(idx / columns);
        const col = idx % columns;
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = {
            x: startX + (col * (option.size.width + spacing)),
            y: startY + (row * (option.size.height + spacing))
          };
        }
      });
    }

    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { ...el, subElements: updatedSubElements } : el
    );

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  const distributeOptions = (elementId, direction = 'vertical', spacing = 40) => {
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element || !element.isUngrouped || !element.subElements) return;

    const options = element.subElements.filter(sub => sub.type === 'question-option');
    if (options.length === 0) return;

    // Sort by position
    options.sort((a, b) => 
      direction === 'vertical' 
        ? a.position.y - b.position.y 
        : a.position.x - b.position.x
    );

    let updatedSubElements = [...element.subElements];
    
    const startX = options[0].position.x;
    const startY = options[0].position.y;

    if (direction === 'vertical') {
      options.forEach((option, idx) => {
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = { x: startX, y: startY + (idx * spacing) };
        }
      });
    } else if (direction === 'grid') {
      const columns = 2;
      // Estimate cell width from longest label
      const optFontSize = element.responseConfig?.optionFontSize || 14;
      const longestLabel = Math.max(...options.map(o => (o.content || '').length));
      const cellW = Math.max(100, longestLabel * optFontSize * 0.65 + 50);
      const cellH = spacing;
      options.forEach((option, idx) => {
        const row = Math.floor(idx / columns);
        const col = idx % columns;
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = { x: startX + (col * (cellW + 16)), y: startY + (row * cellH) };
        }
      });
    } else {
      // Horizontal: accumulate x from each option's estimated content width
      const optFontSize = element.responseConfig?.optionFontSize || 14;
      const optPadding = element.responseConfig?.optionPadding || 8;
      let runningX = startX;
      options.forEach((option) => {
        const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
        if (optionInSub) {
          optionInSub.position = { x: runningX, y: startY };
          const labelW = (option.content || '').length * optFontSize * 0.7 + optPadding * 4 + 40;
          runningX += labelW;
        }
      });
    }

    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { ...el, subElements: updatedSubElements } : el
    );

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  const alignOptions = (elementId, alignment = 'left') => {
    const currentScreen = screens[currentScreenIndex];
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element || !element.isUngrouped || !element.subElements) return;

    const options = element.subElements.filter(sub => sub.type === 'question-option');
    if (options.length === 0) return;

    let updatedSubElements = [...element.subElements];
    
    switch (alignment) {
      case 'left':
        const leftX = Math.min(...options.map(opt => opt.position.x));
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.x = leftX;
        });
        break;
      case 'center': {
        const minX = Math.min(...options.map(opt => opt.position.x));
        const maxX = Math.max(...options.map(opt => opt.position.x + opt.size.width));
        const centerX = (minX + maxX) / 2;
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.x = centerX - (option.size.width / 2);
        });
        break;
      }
      case 'right':
        const rightX = Math.max(...options.map(opt => opt.position.x + opt.size.width));
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.x = rightX - option.size.width;
        });
        break;
      case 'top':
        const topY = Math.min(...options.map(opt => opt.position.y));
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.y = topY;
        });
        break;
      case 'middle': {
        const minY = Math.min(...options.map(opt => opt.position.y));
        const maxY = Math.max(...options.map(opt => opt.position.y + opt.size.height));
        const centerY = (minY + maxY) / 2;
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.y = centerY - (option.size.height / 2);
        });
        break;
      }
      case 'bottom':
        const bottomY = Math.max(...options.map(opt => opt.position.y + opt.size.height));
        options.forEach(option => {
          const optionInSub = updatedSubElements.find(sub => sub.id === option.id);
          if (optionInSub) optionInSub.position.y = bottomY - option.size.height;
        });
        break;
    }

    const updatedElements = currentScreen.elements.map(el =>
      el.id === elementId ? { ...el, subElements: updatedSubElements } : el
    );

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = { ...currentScreen, elements: updatedElements };
    setScreens(updatedScreens);
  };

  // Grouping functions
  const getElementGroup = (elementId) => {
    return elementGroups.find(group => group.includes(elementId));
  };

  const ungroupElement = (elementId) => {
    setElementGroups(elementGroups.filter(group => !group.includes(elementId)));
  };

  return {
    // State
    lockedElements,
    setLockedElements,
    elementGroups,
    setElementGroups,
    showOutOfBoundsWarning,
    setShowOutOfBoundsWarning,
    
    // Element update functions
    updateElementPosition,
    updateElementPositions,
    updateElementSize,
    updateElementContent,
    updateElementStyle,
    updateResponseConfig,
    alignElement,
    
    // Element management
    deleteElement,
    toggleLockElement,
    isElementLocked,
    
    // Question grouping
    ungroupQuestion,
    regroupQuestion,
    updateSubElementPosition,
    updateSubElementSize,
    
    // Layout helpers
    autoArrangeOptions,
    distributeOptions,
    alignOptions,
    
    // Bounds checking
    getOutOfBoundsElements,
    fitElementsToCanvas,
    fitAllScreensToCanvas,
    
    // Element grouping
    getElementGroup,
    ungroupElement,
  };
};

export default useElementOperations;
